import { Box, Chip, Dialog, DialogTitle, DialogContent, IconButton, Collapse, useTheme } from "@mui/material";
import { useState, useEffect, useContext } from "react";
import { Refresh } from "components/common/SVG/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Text from "components/common/Typography/Text";
import { useGlobalRefreshState } from "hooks/usePatientDataRefresh";
import { loader } from "styles/index";
import { FeatureFlagsContext } from "contexts/FeatureFlagsContext";
import { isFeatureEnabled, recentActivityFeature } from "utils/featureFlags";

// TypeScript interfaces
export interface HistoryEntry {
  status: string;
  timestamp: string;
  duration_ms?: number;
}

const container = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "4px 12px",
  maxWidth: "300px",
  minWidth: "200px",
};

// Time constants for relative time formatting
const JUST_NOW_THRESHOLD = 10; // seconds
const SECONDS_THRESHOLD = 60; // seconds
const MINUTES_THRESHOLD = 60; // minutes

const toTitleCase = (str: string) => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Utility function to format timestamps as relative times
const getRelativeTime = (timestamp: string): string => {
  const now = Date.now();
  const time = new Date(timestamp).getTime();
  const diffMs = now - time;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffSec < JUST_NOW_THRESHOLD) return "just now";
  if (diffSec < SECONDS_THRESHOLD) {
    return diffSec === 1 ? "1 second ago" : `${diffSec} seconds ago`;
  }
  if (diffMin === 1) return "1 minute ago";
  if (diffMin < MINUTES_THRESHOLD) return `${diffMin} minutes ago`;
  if (diffHour === 1) return "1 hour ago";
  return `${diffHour} hours ago`;
};

// Format a duration in milliseconds as a short "took Xs" string
const formatStepDuration = (durationMs: number): string => {
  if (!durationMs || durationMs <= 0) return "";
  if (durationMs < 1000) return `took ${durationMs}ms`;

  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return seconds > 0 ? `took ${minutes}m ${seconds}s` : `took ${minutes}m`;
  }
  return `took ${totalSeconds}s`;
};

export const BackgroundProcessStatus = () => {
  const theme = useTheme();
  const activeProcesses = useGlobalRefreshState();
  const processCount = activeProcesses.length;
  const flagsData = useContext(FeatureFlagsContext);
  const recentActivityEnabled = isFeatureEnabled(flagsData, recentActivityFeature);

  // State for dialog and expand/collapse
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedPatients, setExpandedPatients] = useState<Set<string>>(new Set());

  // Close dialog when no active processes
  useEffect(() => {
    if (activeProcesses.length === 0 && isDialogOpen) {
      setIsDialogOpen(false);
      setExpandedPatients(new Set()); // Reset expanded state
    }
  }, [activeProcesses.length, isDialogOpen]);

  // Show nothing if no active processes
  if (activeProcesses.length === 0) {
    return null;
  }

  // Header text logic
  const headerText = processCount === 1 ? "Refreshing Patient Data" : "Refreshing Patients";

  // If feature flag is disabled, show basic text without dialog functionality
  if (!recentActivityEnabled) {
    return (
      <Box sx={{ ...container }}>
        <Text
          sx={{
            fontWeight: 500,
            color: theme.palette.text.secondary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          variant="body2">
          {toTitleCase(headerText) === "Generating Smart Summary" ? "Refreshing Patient Data" : toTitleCase(headerText)}
        </Text>
        {processCount > 1 && (
          <Chip
            label={processCount}
            size="small"
            sx={{
              height: "20px",
              minWidth: "20px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              fontSize: "12px",
              fontWeight: 600,
              "& .MuiChip-label": {
                padding: "0 6px",
              },
            }}
          />
        )}
        <Box sx={{ ...loader }}>
          <Refresh fill={theme.palette.common.blue} width={20} height={20} />
        </Box>
      </Box>
    );
  }

  // Feature flag is enabled - show full functionality with dialog
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const togglePatientExpand = (patientId: string) => {
    setExpandedPatients((prev) => {
      const next = new Set(prev);
      if (next.has(patientId)) {
        next.delete(patientId);
      } else {
        next.add(patientId);
      }
      return next;
    });
  };

  return (
    <>
      <Box sx={{ ...container }}>
        <Text
          sx={{
            fontWeight: 500,
            color: theme.palette.text.secondary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          variant="body2">
          {toTitleCase(headerText) === "Generating Smart Summary" ? "Refreshing Patient Data" : toTitleCase(headerText)}
        </Text>
        {processCount > 1 && (
          <Chip
            label={processCount}
            size="small"
            sx={{
              height: "20px",
              minWidth: "20px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              fontSize: "12px",
              fontWeight: 600,
              "& .MuiChip-label": {
                padding: "0 6px",
              },
            }}
          />
        )}
        <Box sx={{ ...loader }}>
          <Refresh fill={theme.palette.common.blue} width={20} height={20} />
        </Box>
      </Box>

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "80vh",
          },
        }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Text variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Smart Summary Refresh Status
          </Text>
          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: theme.palette.grey[500] }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {activeProcesses.map((process, index) => {
            const isExpanded = expandedPatients.has(process.patientId);
            const latestHistory = process.history && process.history.length > 0 ? process.history[process.history.length - 1] : null;
            // Current step is backend-driven: prefer the latest history entry's status,
            // fall back to the current_step field from WebSocket. No hardcoded default.
            const currentStepText = latestHistory?.status || process.currentStep || "";
            const startTime = process.createdAt ? new Date(process.createdAt).getTime() : Date.now();
            const elapsedTime = getRelativeTime(new Date(startTime).toISOString());

            return (
              <Box
                key={process.patientId}
                sx={{
                  borderBottom: index < activeProcesses.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                }}>
                <Box
                  sx={{
                    p: 2,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                  onClick={() => togglePatientExpand(process.patientId)}>
                  {/* Row 1: Chevron + Dot + Patient Name */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <IconButton size="small" sx={{ p: 0 }}>
                      {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </IconButton>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#FF9800",
                        flexShrink: 0,
                      }}
                    />
                    <Text variant="body1" sx={{ fontWeight: 600, color: "#2196F3", lineHeight: 1.4 }}>
                      {process.patientName}
                    </Text>
                  </Box>

                  {/* Row 2: Current Step (left) | Started time (right) */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pl: 5 }}>
                    <Text variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.4 }}>
                      {toTitleCase(currentStepText)}
                    </Text>
                    <Text variant="body2" sx={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}>
                      Started {elapsedTime}
                    </Text>
                  </Box>
                </Box>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 7, pr: 2, pb: 2, pt: 1 }}>
                    {process.history && process.history.length > 0 ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {process.history
                          .slice()
                          .reverse()
                          .map((entry: HistoryEntry, idx: number) => {
                            // Top (idx === 0) = most recent/currently running step: show "started X ago"
                            // Remaining entries = completed steps: show "took Xs" from duration_ms
                            const isCurrentStep = idx === 0;
                            const hasDuration = typeof entry.duration_ms === "number" && entry.duration_ms > 0;
                            const rightLabel =
                              !isCurrentStep && hasDuration
                                ? formatStepDuration(entry.duration_ms as number)
                                : `started ${getRelativeTime(entry.timestamp)}`;

                            return (
                              <Box
                                key={`${entry.timestamp}-${idx}`}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 2,
                                }}>
                                <Text variant="body2" sx={{ color: theme.palette.text.primary, flex: 1 }}>
                                  {toTitleCase(entry.status)}
                                </Text>
                                <Text
                                  variant="body2"
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    whiteSpace: "nowrap",
                                  }}>
                                  {rightLabel}
                                </Text>
                              </Box>
                            );
                          })}
                      </Box>
                    ) : (
                      <Text variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        No detailed history available
                      </Text>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BackgroundProcessStatus;
